import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { ROOT_URL } from "../../api/ApiConfig";
import { updateKbArticle } from "./updateKbArticle";

export const getKbArticleById = createAsyncThunk(
  "kbArticleDetail/getKbArticleById",
  async ({ articleId }, { getState, rejectWithValue }) => {
    try {
      const { loginToken } = getState();
      const authToken = loginToken?.response?.token;

      if (!articleId) {
        throw new Error("Article ID is required");
      }

      const url = `${ROOT_URL}/kb/article/${articleId}`;

      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "application/json",
        },
      });

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const isPopulatedUser = (value) =>
  Boolean(
    value &&
      typeof value === "object" &&
      (value.full_name || value.name || value.email)
  );

const pickPopulatedUser = (next, prev) => {
  if (isPopulatedUser(next)) return next;
  if (isPopulatedUser(prev)) return prev;
  return next ?? prev;
};

const mergeKbArticle = (prev, next) => {
  if (!next) return prev || next;
  if (!prev) return next;
  return {
    ...prev,
    ...next,
    authorId: pickPopulatedUser(next.authorId, prev.authorId),
    lastEditedBy: pickPopulatedUser(next.lastEditedBy, prev.lastEditedBy),
  };
};

const initialState = {
  loading: false,
  error: null,
  article: null,
  lastFetchedArticleId: null,
  cacheByArticleId: {},
};

const getKbArticleByIdSlice = createSlice({
  name: "getKbArticleById",
  initialState,
  reducers: {
    resetArticleDetail: (state) => {
      // Keep `article` + cache so revisits can render instantly (no skeleton).
      state.loading = false;
      state.error = null;
    },
    invalidateArticleCache: (state, action) => {
      const idStr = action.payload != null ? String(action.payload) : "";
      if (idStr && state.cacheByArticleId[idStr]) {
        delete state.cacheByArticleId[idStr];
      }
      if (
        idStr &&
        state.lastFetchedArticleId != null &&
        String(state.lastFetchedArticleId) === idStr
      ) {
        state.lastFetchedArticleId = null;
      }
      if (
        idStr &&
        state.article &&
        (String(state.article._id) === idStr ||
          (state.article.slug && String(state.article.slug) === idStr))
      ) {
        state.article = null;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getKbArticleById.pending, (state, action) => {
        const idStr =
          action.meta.arg?.articleId != null
            ? String(action.meta.arg.articleId)
            : "";
        const cached = idStr ? state.cacheByArticleId[idStr] : null;
        const currentId =
          state.article?._id != null ? String(state.article._id) : "";
        const currentSlug =
          state.article?.slug != null ? String(state.article.slug) : "";
        const inMemorySame =
          idStr &&
          state.article != null &&
          (currentId === idStr || currentSlug === idStr);

        state.error = null;
        // Background refresh: always fetch. Show cache/in-memory immediately (no skeleton).
        state.loading = true;
        if (cached) {
          state.article = cached;
        } else if (!inMemorySame && idStr && currentId && currentId !== idStr) {
          state.article = null;
        }
      })
      .addCase(getKbArticleById.fulfilled, (state, action) => {
        state.loading = false;
        const data = action.payload?.data || null;
        state.article = data;
        const requestedId =
          action.meta.arg?.articleId != null
            ? String(action.meta.arg.articleId)
            : "";
        if (requestedId && data) {
          state.cacheByArticleId[requestedId] = data;
          if (data._id != null) {
            state.cacheByArticleId[String(data._id)] = data;
          }
          if (data.slug) {
            state.cacheByArticleId[String(data.slug)] = data;
          }
          state.lastFetchedArticleId = requestedId;
        }
      })
      .addCase(getKbArticleById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateKbArticle.fulfilled, (state, action) => {
        const updated = action.payload?.data;
        const requestedId =
          action.meta.arg?.articleId != null
            ? String(action.meta.arg.articleId)
            : "";
        if (!requestedId) return;

        const writeCache = (entry) => {
          if (!entry) return;
          state.cacheByArticleId[requestedId] = entry;
          if (entry._id != null) {
            state.cacheByArticleId[String(entry._id)] = entry;
          }
          if (entry.slug) {
            state.cacheByArticleId[String(entry.slug)] = entry;
          }
        };

        if (updated && typeof updated === "object") {
          const prev =
            (state.article &&
            (String(state.article._id) === requestedId ||
              (state.article.slug &&
                String(state.article.slug) === requestedId))
              ? state.article
              : null) ||
            state.cacheByArticleId[requestedId] ||
            (updated._id != null
              ? state.cacheByArticleId[String(updated._id)]
              : null);
          const merged = mergeKbArticle(prev, updated);
          writeCache(merged);
          if (
            state.article &&
            (String(state.article._id) === requestedId ||
              (state.article.slug &&
                String(state.article.slug) === requestedId))
          ) {
            state.article = merged;
          }
        } else if (
          state.article &&
          (String(state.article._id) === requestedId ||
            (state.article.slug && String(state.article.slug) === requestedId))
        ) {
          const arg = action.meta.arg || {};
          state.article = {
            ...state.article,
            title: arg.title ?? state.article.title,
            content: arg.content ?? state.article.content,
            categoryIds: arg.categoryIds ?? state.article.categoryIds,
            visibility: arg.visibility ?? state.article.visibility,
            tagIds: arg.tagIds ?? state.article.tagIds,
            status: arg.status ?? state.article.status,
            disableComments:
              arg.disableComments ?? state.article.disableComments,
            updatedAt: new Date().toISOString(),
          };
          writeCache(state.article);
        }
      });
  },
});

export const { resetArticleDetail, invalidateArticleCache } =
  getKbArticleByIdSlice.actions;
export default getKbArticleByIdSlice.reducer;
